import { useState, useEffect, useMemo } from 'react'
import { api } from '../lib/api'
import { FacilitatorGuard } from '../components/FacilitatorGuard'
import AdminNav from '../components/AdminNav'
import {
  MagnifyingGlass,
  X,
  CheckCircle,
  Circle,
  UsersThree,
  Student,
  Crown,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react'

interface ClerkUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  createdAt: number
}

interface UserProgress {
  user: { id: string; email: string; firstName: string; lastName: string } | null
  assignments: Array<{ crew: string; team: string; role: string }>
  progress: Array<{ moduleId: string; status: string; percentComplete: number }>
  stats: {
    completedModules: number
    totalModules: number
    avgPercent: number
    avgKcScore: number
  }
}

const CREW_OPTIONS = ['audio', 'video', 'stage', 'it']
const TEAM_OPTIONS = ['installation', 'operations']

function UserDetailModal({
  user,
  onClose,
}: {
  user: ClerkUser
  onClose: () => void
}) {
  const [data, setData] = useState<UserProgress | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getUserProgress(user.id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [user.id])

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{user.firstName} {user.lastName}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {loading ? (
            <p>Loading…</p>
          ) : !data ? (
            <p className="error">Failed to load user details.</p>
          ) : (
            <>
              <div className="user-detail-stats">
                <div className="stat-card">
                  <div className="stat-card__value">{data.stats.completedModules}</div>
                  <div className="stat-card__label">Modules Completed</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card__value">{data.stats.avgPercent}%</div>
                  <div className="stat-card__label">Avg Progress</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card__value">{data.stats.avgKcScore}%</div>
                  <div className="stat-card__label">Avg KC Score</div>
                </div>
              </div>

              <h4>Assignments</h4>
              {data.assignments.length === 0 ? (
                <p className="text-muted">No assignments.</p>
              ) : (
                <ul className="assignment-list">
                  {data.assignments.map((a, i) => (
                    <li key={i} className="assignment-tag">
                      <span className={`badge badge--${a.crew}`}>{a.crew}</span>
                      <span>{a.team}</span>
                      <span className="text-muted">{a.role}</span>
                    </li>
                  ))}
                </ul>
              )}

              <h4>Module Progress</h4>
              {data.progress.length === 0 ? (
                <p className="text-muted">No progress recorded.</p>
              ) : (
                <div className="module-progress-list">
                  {data.progress.map((p) => (
                    <div key={p.moduleId} className="module-progress-row">
                      <span className="module-progress-id">{p.moduleId}</span>
                      <div className="module-progress-bar">
                        <div
                          className="module-progress-fill"
                          style={{ width: `${p.percentComplete}%` }}
                        />
                      </div>
                      <span className="module-progress-pct">{p.percentComplete}%</span>
                      <span
                        className={`status-pill status-pill--${p.status}`}
                      >
                        {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function AssignmentEditor({
  userId,
  currentAssignments,
  onSaved,
  onCancel,
}: {
  userId: string
  currentAssignments: Array<{ crew: string; team: string; role: string }>
  onSaved: () => void
  onCancel: () => void
}) {
  const [assignments, setAssignments] = useState(currentAssignments.length > 0 ? currentAssignments : [{ crew: 'video', team: 'operations', role: 'trainee' }])
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await api.updateUserAssignments(userId, assignments)
      onSaved()
    } catch {
      alert('Failed to update assignments')
      setSaving(false)
    }
  }

  function updateAssignment(index: number, field: string, value: string) {
    setAssignments((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)))
  }

  function addAssignment() {
    setAssignments((prev) => [...prev, { crew: 'video', team: 'operations', role: 'trainee' }])
  }

  function removeAssignment(index: number) {
    setAssignments((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="assignment-editor">
      {assignments.map((a, i) => (
        <div key={i} className="assignment-editor__row">
          <select value={a.crew} onChange={(e) => updateAssignment(i, 'crew', e.target.value)}>
            {CREW_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={a.team} onChange={(e) => updateAssignment(i, 'team', e.target.value)}>
            {TEAM_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input
            type="text"
            value={a.role}
            onChange={(e) => updateAssignment(i, 'role', e.target.value)}
            placeholder="Role"
          />
          <button type="button" className="btn btn-sm btn-danger" onClick={() => removeAssignment(i)}>
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="assignment-editor__actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={addAssignment}>
          + Add Assignment
        </button>
        <div className="spacer" />
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function UserRow({
  user,
  onRoleChange,
  onSelect,
  selected,
  onShowDetail,
}: {
  user: ClerkUser
  onRoleChange: (id: string, role: string) => void
  onSelect: (id: string, selected: boolean) => void
  selected: boolean
  onShowDetail: (user: ClerkUser) => void
}) {
  const [changing, setChanging] = useState(false)
  const [editingAssignments, setEditingAssignments] = useState(false)
  const [assignments, setAssignments] = useState<Array<{ crew: string; team: string; role: string }>>([])

  useEffect(() => {
    api.getUserProgress(user.id)
      .then((data) => setAssignments(data.assignments || []))
      .catch(() => setAssignments([]))
  }, [user.id])

  async function toggleRole() {
    const newRole = user.role === 'facilitator' ? 'trainee' : 'facilitator'
    setChanging(true)
    try {
      await api.updateUserRole(user.id, newRole)
      onRoleChange(user.id, newRole)
    } catch {
      alert('Failed to update role')
    } finally {
      setChanging(false)
    }
  }

  const assignmentLabel = assignments.length > 0
    ? `${assignments[0].crew} / ${assignments[0].team}`
    : '—'

  return (
    <>
      <tr className={selected ? 'row-selected' : ''}>
        <td>
          <label className="row-select">
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelect(user.id, e.target.checked)}
            />
            {selected ? <CheckCircle weight="fill" size={16} /> : <Circle size={16} />}
          </label>
        </td>
        <td>
          <button className="user-name-btn" onClick={() => onShowDetail(user)}>
            {user.firstName} {user.lastName}
          </button>
        </td>
        <td>{user.email}</td>
        <td>
          <span className={`role-pill role-pill--${user.role}`}>
            {user.role === 'facilitator' ? <Crown size={12} /> : <Student size={12} />}
            {user.role}
          </span>
        </td>
        <td>
          <button
            type="button"
            onClick={() => setEditingAssignments((v) => !v)}
            className="btn btn-secondary btn-sm"
          >
            {assignmentLabel}
            {editingAssignments ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
        </td>
        <td>
          <button
            type="button"
            onClick={toggleRole}
            disabled={changing}
            className="btn btn-secondary btn-sm"
          >
            {changing ? 'Saving…' : `Make ${user.role === 'facilitator' ? 'Trainee' : 'Facilitator'}`}
          </button>
        </td>
      </tr>
      {editingAssignments && (
        <tr className="assignment-editor-row">
          <td colSpan={6}>
            <AssignmentEditor
              userId={user.id}
              currentAssignments={assignments}
              onSaved={() => {
                setEditingAssignments(false)
                api.getUserProgress(user.id).then((data) => setAssignments(data.assignments || []))
              }}
              onCancel={() => setEditingAssignments(false)}
            />
          </td>
        </tr>
      )}
    </>
  )
}

function BulkActionsBar({
  selectedIds,
  onClear,
  onRoleChange,
  onAssign,
}: {
  selectedIds: string[]
  onClear: () => void
  onRoleChange: (role: string) => void
  onAssign: (assignments: Array<{ crew: string; team: string; role: string }>) => void
}) {
  const [showAssign, setShowAssign] = useState(false)
  const [assignments, setAssignments] = useState([{ crew: 'video', team: 'operations', role: 'trainee' }])

  if (selectedIds.length === 0) return null

  return (
    <div className="bulk-actions-bar">
      <div className="bulk-actions-info">
        <UsersThree size={18} weight="bold" />
        <span>{selectedIds.length} selected</span>
      </div>
      <div className="bulk-actions-buttons">
        <button className="btn btn-secondary btn-sm" onClick={() => onRoleChange('trainee')}>
          Make Trainees
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => onRoleChange('facilitator')}>
          Make Facilitators
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAssign((v) => !v)}>
          Assign Crew
        </button>
        <button className="btn btn-sm" onClick={onClear}>
          Clear
        </button>
      </div>
      {showAssign && (
        <div className="bulk-assign-panel">
          {assignments.map((a, i) => (
            <div key={i} className="bulk-assign-row">
              <select value={a.crew} onChange={(e) => {
                const newA = [...assignments]
                newA[i] = { ...a, crew: e.target.value }
                setAssignments(newA)
              }}>
                {CREW_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={a.team} onChange={(e) => {
                const newA = [...assignments]
                newA[i] = { ...a, team: e.target.value }
                setAssignments(newA)
              }}>
                {TEAM_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                type="text"
                value={a.role}
                onChange={(e) => {
                  const newA = [...assignments]
                  newA[i] = { ...a, role: e.target.value }
                  setAssignments(newA)
                }}
                placeholder="Role"
              />
            </div>
          ))}
          <div className="bulk-assign-actions">
            <button className="btn btn-primary btn-sm" onClick={() => { onAssign(assignments); setShowAssign(false) }}>
              Apply to {selectedIds.length} Users
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function UsersTable() {
  const [users, setUsers] = useState<ClerkUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | 'all'>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [detailUser, setDetailUser] = useState<ClerkUser | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getUsers({ limit: 500 })
        setUsers(data.users)
      } catch (err) {
        console.error('Failed to load users:', err)
        setError(err instanceof Error ? err.message : 'Failed to load users')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filteredUsers = useMemo(() => {
    let result = users
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (u) =>
          (u.firstName || '').toLowerCase().includes(q) ||
          (u.lastName || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q),
      )
    }
    if (roleFilter !== 'all') {
      result = result.filter((u) => u.role === roleFilter)
    }
    return result
  }, [users, search, roleFilter])

  function handleRoleChange(id: string, newRole: string) {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role: newRole } : u)))
  }

  function handleSelect(id: string, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) next.add(id)
      else next.delete(id)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    if (checked) {
      setSelectedIds(new Set(filteredUsers.map((u) => u.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  async function handleBulkRoleChange(role: string) {
    if (!confirm(`Change role to ${role} for ${selectedIds.size} users?`)) return
    try {
      await api.bulkUpdateUserRoles(Array.from(selectedIds), role)
      setUsers((prev) => prev.map((u) => (selectedIds.has(u.id) ? { ...u, role } : u)))
      setSelectedIds(new Set())
    } catch {
      alert('Failed to update roles')
    }
  }

  async function handleBulkAssign(assignments: Array<{ crew: string; team: string; role: string }>) {
    if (!confirm(`Assign ${assignments.length} assignment(s) to ${selectedIds.size} users?`)) return
    try {
      await api.bulkAssignUsers(Array.from(selectedIds), assignments)
      setSelectedIds(new Set())
    } catch {
      alert('Failed to assign users')
    }
  }

  const allSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id))

  if (loading) return <p>Loading users…</p>
  if (error) return <p className="error">Error: {error}</p>

  return (
    <div>
      <div className="users-toolbar">
        <div className="users-search">
          <MagnifyingGlass size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="search-clear" onClick={() => setSearch('')}>
              <X size={14} />
            </button>
          )}
        </div>
        <div className="users-filters">
          <button
            className={`role-chip ${roleFilter === 'all' ? 'role-chip--active' : ''}`}
            onClick={() => setRoleFilter('all')}
          >
            All
          </button>
          <button
            className={`role-chip ${roleFilter === 'trainee' ? 'role-chip--active' : ''}`}
            onClick={() => setRoleFilter('trainee')}
          >
            Trainees
          </button>
          <button
            className={`role-chip ${roleFilter === 'facilitator' ? 'role-chip--active' : ''}`}
            onClick={() => setRoleFilter('facilitator')}
          >
            Facilitators
          </button>
        </div>
      </div>

      <BulkActionsBar
        selectedIds={Array.from(selectedIds)}
        onClear={() => setSelectedIds(new Set())}
        onRoleChange={handleBulkRoleChange}
        onAssign={handleBulkAssign}
      />

      <div className="users-count">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      {filteredUsers.length === 0 ? (
        <p className="empty-state">No users match your filters.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>
                <label className="row-select">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  {allSelected ? <CheckCircle weight="fill" size={16} /> : <Circle size={16} />}
                </label>
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Assignment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                onRoleChange={handleRoleChange}
                onSelect={handleSelect}
                selected={selectedIds.has(user.id)}
                onShowDetail={setDetailUser}
              />
            ))}
          </tbody>
        </table>
      )}

      {detailUser && (
        <UserDetailModal user={detailUser} onClose={() => setDetailUser(null)} />
      )}
    </div>
  )
}

export default function AdminUsersPage() {
  return (
    <FacilitatorGuard>
      <div className="admin-page">
        <h1>User Management</h1>
        <p>View and manage trainee and facilitator roles.</p>
        <AdminNav />
        <UsersTable />
      </div>
    </FacilitatorGuard>
  )
}
